import { Badge, Button, Select } from '@react-apps/design-system';
import {
  PRODUCT_PERK_OVERDUE_POLICIES,
  type GrantPolicySpec,
  type ProductPerkOverduePolicy,
} from '../../../domain/entities/catalog.entity';
import {
  PERK_ASSOCIATION_BATCH_ERROR_LABELS,
  type PerkAssociationBatchError,
} from '../../../domain/perk-association-batch';
import {
  DELIVERS_PER_CYCLE_LABELS,
  OVERDUE_POLICY_LABELS,
  grantPolicyLabel,
  perkTypeLabel,
} from '../../labels';
import type { ResolvedPerk } from '../../perk-index';
import { GrantPolicyParamsFields } from '../GrantPolicyParamsFields';
import { ScopeListInput } from '../ScopeListInput';
import type { PendingPerkRow, PerkAssociationExtraField } from './types';

interface PendingPerkCardProps {
  row: PendingPerkRow;
  resolved: ResolvedPerk | undefined;
  spec: GrantPolicySpec | undefined;
  supportsTargetScopes: boolean;
  /** Campos exclusivos do alvo — vazio nos alvos que não os têm. */
  extraFields: readonly PerkAssociationExtraField[];
  /** Preenchido quando o back reprovou **este** índice do último `add[]`. */
  error: PerkAssociationBatchError | undefined;
  /** Duplicata detectada aqui, antes do round-trip. */
  duplicateReason: string | null;
  /** Aviso do alvo que não impede aplicar (`PerkAssociationTarget.pendingNotice`). */
  notice?: string | null;
  onChange: (patch: Partial<PendingPerkRow>) => void;
  onDiscard: () => void;
}

/**
 * Uma linha em staging. Diferente das existentes, ela é **editável** até o
 * "Aplicar": os campos que aparecem saem da spec do kind, não de uma tabela
 * local, e o que o kind proíbe nem chega a virar campo.
 */
export function PendingPerkCard({
  row,
  resolved,
  spec,
  supportsTargetScopes,
  extraFields,
  error,
  duplicateReason,
  notice = null,
  onChange,
  onDiscard,
}: PendingPerkCardProps) {
  const hasProblem = Boolean(error) || Boolean(duplicateReason);

  return (
    <li
      className={`space-y-3 rounded-md border px-3 py-3 ${
        hasProblem
          ? 'border-red-400 bg-red-50/40 dark:bg-red-950/20'
          : 'border-(--color-border)'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-(--color-text-primary) block truncate text-sm font-medium">
            {resolved?.group.name ?? resolved?.group.key ?? row.perkId}
          </span>
          <span className="text-(--color-text-secondary) block truncate text-xs">
            {resolved
              ? `${perkTypeLabel(resolved.group.type)} · ${grantPolicyLabel(
                  resolved.form.grantPolicyKind,
                )}`
              : 'Perk fora do catálogo'}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="info">Novo</Badge>
          <Button type="button" size="sm" variant="ghost" onClick={onDiscard}>
            Descartar
          </Button>
        </div>
      </div>

      <GrantPolicyParamsFields
        spec={spec}
        draft={row.values}
        onChange={(values) => onChange({ values })}
        emptyMessage="Esta forma de concessão não pede limites."
      />

      {extraFields.includes('overduePolicy') && (
        <Select
          label="Política de inadimplência"
          value={row.overduePolicy}
          onChange={(e) =>
            onChange({
              overduePolicy: e.target.value as ProductPerkOverduePolicy,
            })
          }
          options={PRODUCT_PERK_OVERDUE_POLICIES.map((p) => ({
            value: p,
            label: OVERDUE_POLICY_LABELS[p],
          }))}
        />
      )}

      {extraFields.includes('deliversPerCycle') && (
        <Select
          label="Entrega por ciclo"
          value={row.deliversPerCycle ? 'true' : 'false'}
          onChange={(e) =>
            onChange({ deliversPerCycle: e.target.value === 'true' })
          }
          helperText="Curso parcelado entrega uma vez; assinatura reentrega a cada renovação."
          options={[
            { value: 'false', label: DELIVERS_PER_CYCLE_LABELS.false },
            { value: 'true', label: DELIVERS_PER_CYCLE_LABELS.true },
          ]}
        />
      )}

      {supportsTargetScopes && (
        <ScopeListInput
          label="Scopes alvo"
          values={row.targetScopes}
          helperText="Opcional. Scopes em que este perk é concedido."
          onChange={(targetScopes) => onChange({ targetScopes })}
        />
      )}

      {duplicateReason && (
        <p className="text-sm text-amber-600">{duplicateReason}</p>
      )}

      {notice && (
        <p role="status" className="text-sm text-amber-700 dark:text-amber-400">
          {notice}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600">
          {PERK_ASSOCIATION_BATCH_ERROR_LABELS[error.code]}
          <span className="text-(--color-text-secondary) block text-xs">
            {error.message}
          </span>
        </p>
      )}
    </li>
  );
}
